/**
 * Planejamento de instalações.
 *
 * Responde, em ordem, às perguntas de quem monta a agenda da equipe:
 *  1. qual o tamanho do problema — números de cabeçalho;
 *  2. ONDE estão as pendências e há quanto tempo — cidades com o tempo de
 *     espera empilhado por faixa. Escolher a cidade abre, num modal, os
 *     bairros e a lista de visita na ordem de atendimento (mais antigo
 *     primeiro);
 *  3. o perfil do que falta — tipo de veículo, consultor e mês de entrada.
 *
 * Tudo é derivado da mesma resposta da aba Controle, no navegador: são algumas
 * centenas de placas, e agregar aqui evita uma rota a mais e mantém os números
 * idênticos entre as abas.
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircleCheck, Clock, MapPin, Phone, Receipt, Wrench } from 'lucide-react';
import { formatarTelefone, moeda, numero, percentual } from '../../lib/format.ts';
import { cx } from '../../lib/cx.ts';
import { Card, Esqueleto, EstadoVazio, TituloSecao } from '../../components/ui/index.tsx';
import Folha from '../../components/ui/Folha.tsx';
import { CartaoDado, GradeBento } from '../../components/ui/Bento.tsx';
import {
  BarrasPorPeriodo,
  corSerie,
  RankingBarras,
  RoscaMix,
} from '../../components/charts/index.tsx';
import {
  chaveCidade,
  contatoPrincipal,
  FAIXAS,
  ROTULO_FAIXA,
  rotuloCidade,
  TOM_FAIXA,
  useDadosRastreamento,
  type Faixa,
  type Item,
} from './dados.ts';

/** Cidades com barra própria; o resto vira uma linha "outras". */
const CIDADES_VISIVEIS = 10;

interface Cidade {
  chave: string;
  rotulo: string;
  itens: Item[];
  porFaixa: Record<Faixa, number>;
}

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

export default function Planejamento() {
  const { data, isLoading } = useDadosRastreamento();
  const [selecionada, setSelecionada] = useState<string | null>(null);
  const fecharCidade = useCallback(() => setSelecionada(null), []);
  const navegar = useNavigate();

  const pendentes = useMemo(
    () => (data?.itens ?? []).filter((i) => i.caso === 'falta_instalar'),
    [data],
  );

  const cidades = useMemo(() => {
    const mapa = new Map<string, Cidade>();
    for (const i of pendentes) {
      const k = chaveCidade(i.associado);
      let c = mapa.get(k);
      if (!c) {
        c = {
          chave: k,
          rotulo: rotuloCidade(i.associado),
          itens: [],
          porFaixa: { ate7: 0, ate15: 0, ate30: 0, mais30: 0 },
        };
        mapa.set(k, c);
      }
      c.itens.push(i);
      if (i.faixa) c.porFaixa[i.faixa]++;
    }
    return [...mapa.values()].sort((a, b) => b.itens.length - a.itens.length);
  }, [pendentes]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Esqueleto key={i} className="h-24" />
          ))}
        </div>
        <Esqueleto className="h-80" />
      </div>
    );
  }

  const r = data?.resumo;
  if (!r || pendentes.length === 0) {
    return (
      <Card className="p-2">
        <EstadoVazio
          icone={<MapPin size={20} />}
          titulo="Nenhuma instalação pendente"
          descricao="Toda placa com produto de rastreador está marcada como instalada neste recorte."
        />
      </Card>
    );
  }

  const receita = pendentes.reduce((s, i) => s + (i.valorProdutos ?? 0), 0);
  const cidade = cidades.find((c) => c.chave === selecionada) ?? null;

  return (
    <div className="flex flex-col gap-5">
      {/* ── 1. Cabeçalho ── */}
      <GradeBento>
        <CartaoDado
          Icone={Wrench}
          tom="perigo"
          rotulo="Falta instalar"
          valor={numero(pendentes.length)}
          nota={`em ${numero(cidades.length)} ${cidades.length === 1 ? 'cidade' : 'cidades'}`}
        />
        <CartaoDado
          Icone={Receipt}
          tom="alerta"
          rotulo="Cobrado sem instalação"
          valor={moeda(receita)}
          nota="por mês, em produto de rastreador"
        />
        <CartaoDado
          Icone={Clock}
          rotulo="Espera mediana"
          valor={`${numero(r.semInstalar.mediana)} dias`}
          nota={`mais antiga: ${numero(r.semInstalar.maisAntigo)} dias`}
        />
        <CartaoDado
          Icone={CircleCheck}
          tom="sucesso"
          rotulo="Instalados"
          valor={percentual(r.comProduto ? (r.ok / r.comProduto) * 100 : 0)}
          nota={`${numero(r.ok)} de ${numero(r.comProduto)} com produto`}
        />
      </GradeBento>

      {/* ── 2. Onde ── */}
      <Card className="p-4">
        <TituloSecao>Pendências por cidade</TituloSecao>
        <p className="-mt-2 mb-3 text-[13px] text-[var(--muted)]">
          Toque numa cidade para ver os bairros e a lista de visita.
        </p>
        <LegendaFaixas />
        <CidadesEmpilhadas cidades={cidades} selecionada={selecionada} onEscolher={setSelecionada} />
      </Card>

      {/* Modal, não painel ao lado: no celular o painel caía abaixo da lista e
          a pessoa tocava na cidade sem ver nada mudar. */}
      <Folha
        aberta={cidade !== null}
        larga
        titulo={cidade ? `${cidade.rotulo} · ${numero(cidade.itens.length)} para instalar` : ''}
        onFechar={fecharCidade}
      >
        {cidade && (
          <PlanoDeVisita
            cidade={cidade}
            onMapa={() => navegar(`/rastreamento/mapa?cidade=${encodeURIComponent(cidade.chave)}`)}
          />
        )}
      </Folha>

      {/* ── 3. Perfil ── */}
      <div className="grid gap-3 lg:grid-cols-2">
        <Card className="p-4">
          <TituloSecao>Por tipo de veículo</TituloSecao>
          <RoscaMix fatias={agrupar(pendentes, (i) => i.tipo ?? 'Não informado')} />
        </Card>
        <Card className="p-4">
          <TituloSecao>Por consultor da carteira</TituloSecao>
          <RankingBarras
            itens={agrupar(pendentes, (i) => i.consultor ?? 'Sem consultor').slice(0, 10)}
            rotuloColuna="Consultor"
            rotuloMedida="Pendências"
          />
        </Card>
      </div>

      <Card className="p-4">
        <TituloSecao>Quando as placas pendentes entraram</TituloSecao>
        <p className="-mt-1 mb-3 text-[13px] text-[var(--muted)]">
          Mês de cadastro no SGA. Um mês alto e antigo é estoque parado; um mês recente alto é
          fluxo que a equipe não está acompanhando.
        </p>
        <BarrasPorPeriodo
          linhas={porMes(pendentes)}
          series={[{ chave: 'placas', rotulo: 'Placas', cor: corSerie(0) }]}
          formatar={(v) => numero(v)}
        />
      </Card>
    </div>
  );
}

// ─────────────────────────── Agregações ───────────────────────────

function agrupar(itens: Item[], chave: (i: Item) => string) {
  const m = new Map<string, number>();
  for (const i of itens) m.set(chave(i), (m.get(chave(i)) ?? 0) + 1);
  return [...m]
    .map(([rotulo, valor]) => ({ rotulo, valor }))
    .sort((a, b) => b.valor - a.valor);
}

function porMes(itens: Item[]) {
  const m = new Map<string, number>();
  for (const i of itens) {
    if (!i.dataCadastro) continue;
    const k = i.dataCadastro.slice(0, 7);
    m.set(k, (m.get(k) ?? 0) + 1);
  }
  return [...m]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, placas]) => ({
      rotulo: `${MESES[Number(k.slice(5, 7)) - 1]}/${k.slice(2, 4)}`,
      valores: { placas },
    }));
}

// ─────────────────────────── Peças ───────────────────────────

function LegendaFaixas() {
  return (
    <ul className="-mt-1 mb-4 flex list-none flex-wrap gap-x-4 gap-y-1 p-0">
      {FAIXAS.map((f) => (
        <li key={f} className="flex items-center gap-1.5 text-xs text-[var(--muted)]">
          <span aria-hidden className="size-2 rounded-full" style={{ background: TOM_FAIXA[f] }} />
          {ROTULO_FAIXA[f]}
        </li>
      ))}
    </ul>
  );
}

/**
 * Uma barra por cidade, empilhada pelas faixas de espera.
 *
 * A barra é proporcional à MAIOR cidade, não a 100%: a comparação que importa
 * é entre cidades ("onde mandar a equipe primeiro"), e normalizar cada linha
 * esconderia que uma tem 80 placas e a outra 3. O total fica escrito ao fim, e
 * a decomposição por faixa aparece sob a linha ao passar o mouse ou tocar.
 */
function CidadesEmpilhadas({
  cidades,
  selecionada,
  onEscolher,
}: {
  cidades: Cidade[];
  selecionada: string | null;
  onEscolher: (chave: string) => void;
}) {
  const [foco, setFoco] = useState<string | null>(null);
  const visiveis = cidades.slice(0, CIDADES_VISIVEIS);
  const resto = cidades.slice(CIDADES_VISIVEIS);
  const maior = Math.max(...visiveis.map((c) => c.itens.length), 1);

  return (
    <div>
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {visiveis.map((c) => {
          const ativa = selecionada === c.chave;
          const detalhe = foco === c.chave || ativa;
          return (
            <li key={c.chave}>
              <button
                onClick={() => onEscolher(c.chave)}
                onMouseEnter={() => setFoco(c.chave)}
                onMouseLeave={() => setFoco(null)}
                onFocus={() => setFoco(c.chave)}
                onBlur={() => setFoco(null)}
                aria-pressed={ativa}
                className={cx(
                  'anel-foco grid w-full grid-cols-[minmax(0,11rem)_minmax(0,1fr)_2.5rem] items-center gap-3 rounded-[var(--r-md)] border border-transparent px-2 py-1.5 text-left transition-colors',
                  ativa ? 'selecionado' : 'hover:bg-[var(--surface-hover)]',
                )}
              >
                <span
                  className={cx(
                    'truncate text-[13px]',
                    ativa ? 'font-medium text-[var(--fg)]' : 'text-[var(--fg)]',
                  )}
                >
                  {c.rotulo}
                </span>
                <span
                  className="flex h-3 gap-[2px]"
                  style={{ width: `${(c.itens.length / maior) * 100}%` }}
                >
                  {FAIXAS.map((f) =>
                    c.porFaixa[f] === 0 ? null : (
                      <span
                        key={f}
                        className="h-full min-w-[3px] first:rounded-l-[4px] last:rounded-r-[4px]"
                        style={{ flexGrow: c.porFaixa[f], background: TOM_FAIXA[f] }}
                      />
                    ),
                  )}
                </span>
                <span className="num text-right text-[13px] text-[var(--fg)]">
                  {numero(c.itens.length)}
                </span>
              </button>
              {detalhe && (
                <p className="num pb-1 pl-2 text-xs text-[var(--muted)] md:pl-[calc(11rem+20px)]">
                  {FAIXAS.filter((f) => c.porFaixa[f] > 0)
                    .map((f) => `${ROTULO_FAIXA[f]}: ${c.porFaixa[f]}`)
                    .join(' · ')}
                </p>
              )}
            </li>
          );
        })}
      </ul>
      {resto.length > 0 && (
        <p className="mt-3 px-2 text-xs text-[var(--faint)]">
          + {numero(resto.length)} cidades com{' '}
          {numero(resto.reduce((s, c) => s + c.itens.length, 0))} placas:{' '}
          {resto
            .slice(0, 6)
            .map((c) => c.rotulo)
            .join(', ')}
          {resto.length > 6 ? '…' : ''}
        </p>
      )}
    </div>
  );
}

/** Bairros e lista de visita da cidade escolhida, mais antigo primeiro. */
function PlanoDeVisita({ cidade, onMapa }: { cidade: Cidade; onMapa: () => void }) {
  const bairros = agrupar(cidade.itens, (i) => i.associado.bairro ?? 'Sem bairro').slice(0, 6);
  const fila = [...cidade.itens].sort(
    (a, b) => (b.diasSemInstalar ?? 0) - (a.diasSemInstalar ?? 0),
  );

  return (
    <div className="flex flex-col gap-5 p-1">
      <button
        onClick={onMapa}
        className="anel-foco inline-flex h-10 items-center justify-center gap-1.5 self-start rounded-[var(--r-md)] border border-[var(--border-strong)] px-3 text-[13px] font-medium text-[var(--fg)] hover:bg-[var(--surface-hover)]"
      >
        <MapPin size={14} /> Ver no mapa
      </button>

      <div>
        <p className="mb-2 text-xs font-medium tracking-wide text-[var(--faint)] uppercase">
          Bairros
        </p>
        <RankingBarras itens={bairros} rotuloColuna="Bairro" rotuloMedida="Placas" />
      </div>

      <div>
        <p className="mb-2 text-xs font-medium tracking-wide text-[var(--faint)] uppercase">
          Lista de visita
        </p>
        <ol className="m-0 flex list-none flex-col divide-y divide-[var(--border)] p-0">
          {fila.map((i) => {
            const tel = contatoPrincipal(i.associado);
            return (
              <li key={i.placa} className="flex items-center gap-3 py-2">
                <span
                  className="num w-10 shrink-0 text-right text-[13px] font-semibold"
                  style={{ color: i.faixa ? TOM_FAIXA[i.faixa] : undefined }}
                >
                  {numero(i.diasSemInstalar)}d
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] text-[var(--fg)]">
                    <span className="num font-medium">{i.placa}</span> · {i.associado.nome}
                  </span>
                  <span className="block truncate text-xs text-[var(--faint)]">
                    {i.associado.bairro ?? 'sem bairro'} · {i.veiculo}
                  </span>
                </span>
                {tel && (
                  <a
                    href={`tel:${tel.replace(/\D/g, '')}`}
                    aria-label={`Ligar para ${formatarTelefone(tel)}`}
                    className="anel-foco grid size-10 shrink-0 place-items-center rounded-[var(--r-md)] border border-[var(--border)] text-[var(--fg)] hover:bg-[var(--surface-hover)]"
                  >
                    <Phone size={15} />
                  </a>
                )}
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
